import React, { useState } from "react";

const TabBarWithArrows: React.FC<{
  children: React.ReactNode;
  variant?: "tabs" | "footer";
}> = ({ children, variant = "tabs" }) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateArrows = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateArrows();
    const raf = requestAnimationFrame(() => updateArrows());
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    el.addEventListener("scroll", updateArrows);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      el.removeEventListener("scroll", updateArrows);
    };
  }, [updateArrows]);

  const scroll = (dir: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) return;
    const step = Math.max(180, el.clientWidth * 0.4);
    el.scrollBy({ left: dir === "left" ? -step : step, behavior: "smooth" });
  };

  const arrowClass =
    "shrink-0 w-8 h-8 flex items-center justify-center rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 border-0 cursor-pointer transition-colors";
  const isFooter = variant === "footer";
  return (
    <div className={`flex items-center gap-0 ${isFooter ? "" : "mt-3 -mb-px"}`}>
      {canScrollLeft && (
        <button
          type="button"
          onClick={() => scroll("left")}
          className={arrowClass}
          aria-label="Scroll tabs left"
        >
          <span className="text-lg leading-none">&lsaquo;</span>
        </button>
      )}
      <div
        ref={scrollRef}
        className="flex items-center gap-1 overflow-x-auto scrollbar-hide min-w-0 flex-1"
      >
        {children}
      </div>
      {canScrollRight && (
        <button
          type="button"
          onClick={() => scroll("right")}
          className={arrowClass}
          aria-label="Scroll tabs right"
        >
          <span className="text-lg leading-none">&rsaquo;</span>
        </button>
      )}
    </div>
  );
};

export default TabBarWithArrows;
